import { expandRows, type Table } from "@tanstack/vue-table";

function escapeCSVValue(value: unknown): string {
	if (value == null) {
		return "";
	}

	const str = String(value);

	if (/[",\n]/.test(str)) {
		const escaped = str.replace(/"/g, '""');
		return `"${escaped}"`;
	}

	return str;
}

export function generateCSV(table: Table<any>): string[][] {
	const csv: string[][] = [];

	const headerCSV: string[] = [];
	for (const group of table.getHeaderGroups()) {
		for (const headerDef of group.headers) {
			const { header, meta } = headerDef.column.columnDef;
			if (!meta?.csv) continue;

			headerCSV.push(typeof header === "string" ? header : "");
		}
	}
	csv.push(headerCSV);

	// Remember which rows were expended.
	const wasExpended = table.getState().expanded;

	// Expend all row, so everything can be grabbed for csv rendering
	table.toggleAllRowsExpanded(true);

	// PrePaginationRowModel apply all the sorting and filtering
	// We want everything from the table, so we skip pagination
	const preExpandedModel = table.getPreExpandedRowModel();

	// getExpandedRowModel don't expand rows if the paginateExpandedRows is set to false.
	// Thankfully, the expandRows function is exported from tanstack, so we can do it manually here.
	//
	// issue: CU-86dwvdx3p
	const expandedModel = expandRows(preExpandedModel);

	for (const row of expandedModel.rows) {
		const rowCSV: string[] = [];
		for (const cellDef of row.getVisibleCells()) {
			const { meta, cell } = cellDef.column.columnDef;
			if (!meta?.csv) continue;

			const ctx = cellDef.getContext();
			// CSV function is provided, generate from it
			let columnVal: unknown = "";

			if (typeof meta.csv === "function") {
				columnVal = meta.csv(ctx);
			} else if (typeof cell === "function") {
				const el = cell(ctx);

				// We fallback to getValue, since most of generated cell are vNode.
				columnVal = typeof el === "string" ? el : ctx.getValue();
			} else if (typeof cell === "string" || typeof cell === "number") {
				columnVal = cell;
			} else {
				columnVal = ctx.getValue();
			}

			rowCSV.push(escapeCSVValue(columnVal));
		}

		csv.push(rowCSV);
	}

	// Reset expended state and manually expend rows that were expended before
	table.toggleAllRowsExpanded(false);
	if (typeof wasExpended === "object") {
		for (const [id, state] of Object.entries(wasExpended)) {
			table.getRow(id).toggleExpanded(state);
		}
	}

	return csv;
}
